import React, {Component} from "react"
import {Col, Row, Table} from "react-bootstrap"
import {get} from "lodash"

import {Poll, Recognition, RewardCatalogueProduct, RewardCatalogueReward, RewardWallet} from "@irati/types"
import {Select, Radio} from "@irati/ui/src/FormItem"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Close from "@irati/ui/src/Close"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import OccasionsSelect from "@irati-web/fields/occasions/OccasionsSelect"
import rewardWalletTracking from "@irati-tracking/wallet/rewardWallet"

import {PaymentType} from "../enums"
import RewardSelect from "./Picker"
import {customRewardThumbnail} from "./Search"
import {buildProductOptions, getTotal} from "../utils"
import {RewardThumbnail, TitleContainer, InputWrapper, H4} from "./styled"
import getCurrencyForReward from "@irati/reward/helpers/reward/currency"

interface Props extends InjectIntl {
  product?: RewardCatalogueProduct,
  reward?: RewardCatalogueReward,
  customRewardName?: string,
  rewardWallet?: RewardWallet,
  addRewardEntity?: Recognition | Poll,
  selectedOccasion?: string,
  recipientsCount: number,
  setProduct: (product?: RewardCatalogueProduct) => void,
  setReward: (reward?: RewardCatalogueReward) => void,
  setPaymentMethod: (paymentType: PaymentType) => void,
  paymentMethod: PaymentType,
  setOccasion: (selectedOccasion: string) => void,
  isCredit: boolean
}

interface PaymentOption {
  label: string,
  value: PaymentType
}

interface RewardOptionLabel {
  style: string,
  currency: string
}

interface RewardOption {
  label: RewardOptionLabel,
  value: string,
  isCredit: boolean
}

interface State {
  selectedOption: string | null,
  paymentOptions: PaymentOption[],
  rewardOptions: RewardOption[],
  clickDenominationEventTracked: boolean
}

const cardPaymentOption = {label: <FormattedMessage id="reward.checkout.paymentMethod.card.label" />, value: PaymentType.NEW_CARD}
const walletPaymentOption = {label: <FormattedMessage id="reward.checkout.paymentMethod.wallet.label" />, value: PaymentType.REWARD_WALLET}

class Checkout extends Component<Props, State> {
  state: State = {
    paymentOptions: [],
    rewardOptions: [],
    selectedOption: null,
    clickDenominationEventTracked: false
  }

  componentDidMount() {
    const {reward, product, recipientsCount, intl, rewardWallet} = this.props
    const availableWalletBalance = get(rewardWallet, "availableBalance")

    if (reward && product) {
      const selectedOption = product.uuid
      const paymentOptions = getPaymentOptions(reward, product, availableWalletBalance, recipientsCount)
      const rewardOptions = buildProductOptions(reward, intl)
      this.setState({
        paymentOptions,
        selectedOption,
        rewardOptions
      })
    }
  }

  static getDerivedStateFromProps(nextProps: Props) {
    const {reward, product, recipientsCount, intl, paymentMethod, rewardWallet} = nextProps
    const availableWalletBalance = get(rewardWallet, "availableBalance")
    if (reward) {
      const selectedOption = get(product, "uuid")
      const paymentOptions = getPaymentOptions(reward, product, availableWalletBalance, recipientsCount)
      const rewardOptions = buildProductOptions(reward, intl)

      if (reward.isCredit && paymentMethod !== PaymentType.REWARD_WALLET) {
        nextProps.setPaymentMethod(PaymentType.REWARD_WALLET)
      }

      return {
        paymentOptions,
        selectedOption,
        rewardOptions
      }
    }
    return {
      paymentOptions: [],
      selectedOption: null
    }
  }

  handleSelectClick = () => {
    if (this.props.isCredit && !this.state.clickDenominationEventTracked) {
      this.setState({clickDenominationEventTracked: true})
      rewardWalletTracking.clickDenominationDropdownCheckout()
    }
  }

  handleSelectOnChange = (selectedOption: string) => {
    const {reward, setProduct, isCredit} = this.props
    const products = get(reward, "products.results")
    const selectedProduct = products.find((product: RewardCatalogueProduct) => product.uuid === selectedOption)
    if (isCredit) {
      rewardWalletTracking.selectDenominationCheckout()
    }

    setProduct(selectedProduct)
  }

  handleClose = () => {
    const {isCredit, setReward} = this.props
    if (isCredit) {
      rewardWalletTracking.clearReward()
    }
    setReward()
  }

  render() {
    const {
      reward,
      product,
      setReward,
      setPaymentMethod,
      paymentMethod,
      customRewardName,
      recipientsCount,
      rewardWallet,
      addRewardEntity,
      setOccasion,
      selectedOccasion
    } = this.props
    const {selectedOption, paymentOptions, rewardOptions} = this.state
    const costTotal = getTotal(product, recipientsCount)
    const availableWalletBalance = get(rewardWallet, "availableBalance") || 0
    const showWalletDetails = product && paymentMethod === PaymentType.REWARD_WALLET && costTotal <= availableWalletBalance
    const currencyCode = getCurrencyForReward(reward!)

    return (
      <>
        {addRewardEntity && (
          <InputWrapper>
            <OccasionsSelect selectedOption={selectedOccasion} onChange={setOccasion} />
          </InputWrapper>
        )}
        {!reward && !customRewardName && (
          <InputWrapper>
            <RewardSelect onChange={setReward} />
          </InputWrapper>
        )}

        {reward && (
          <InputWrapper>
            <Col xs={2}>
              <RewardThumbnail src={reward.thumbnailImage} alt={reward.header} />
            </Col>
            <Col xs={10}>
              <TitleContainer>
                <h5>{reward.header}</h5>
                <Close onClick={this.handleClose} />
              </TitleContainer>
              <div onClick={this.handleSelectClick}>
                <Select
                  label="reward.detail.select.label"
                  name="product-id"
                  options={rewardOptions}
                  onChange={this.handleSelectOnChange}
                  isClearable={false}
                  value={selectedOption} />
              </div>
            </Col>
          </InputWrapper>
        )}

        {customRewardName && (
          <Row>
            <Col xs={2}>
              <RewardThumbnail src={customRewardThumbnail} alt={customRewardName} />
            </Col>
            <Col xs={10}>
              <h4>
                <FormattedMessage id="reward.fields.reward.customReward.title" values={{custom: customRewardName}} />
              </h4>
              <p>
                <FormattedMessage id="reward.fields.reward.customReward.Hint" />
              </p>
            </Col>
          </Row>
        )}

        {!customRewardName && product && (
          <>
            <Row>
              <Col md={12}>
                <H4>
                  <FormattedMessage id="reward.checkout.paymentMethod.label" />
                </H4>
              </Col>
              <Col md={12}>
                {!!paymentOptions.length && (
                  <Radio
                    name="Payment Method"
                    value={paymentMethod}
                    options={paymentOptions}
                    onChange={(paymentType: PaymentType) => setPaymentMethod(paymentType)} />
                )}
              </Col>
            </Row>

            {showWalletDetails && (
              <Row>
                <Col md={4}>
                  <Table>
                    <tr>
                      <td>
                        <FormattedMessage id="reward.checkout.walletBalance" />
                      </td>
                      <td>{formatCurrency({value: availableWalletBalance, isCents: false, currencyCode})}</td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedMessage id="reward.checkout.totalToPay" />
                      </td>
                      <td>{formatCurrency({value: costTotal, isCents: false, currencyCode})}</td>
                    </tr>
                    <tr>
                      <td>
                        <FormattedMessage id="reward.checkout.newBalance" />
                      </td>
                      <td>{formatCurrency({value: availableWalletBalance - costTotal, isCents: false, currencyCode})}</td>
                    </tr>
                  </Table>
                </Col>
              </Row>
            )}
          </>
        )}
      </>
    )
  }
}

export default injectIntl(Checkout)

const getPaymentOptions = (reward, product, availableWalletBalance = 0, recipientsCount) => {
  if (!product) {
    return []
  }
  const costTotal = product.price * recipientsCount
  const paymentOptions = []
  if (!reward.isCredit) {
    paymentOptions.push(cardPaymentOption)
  }
  if (costTotal <= availableWalletBalance) {
    paymentOptions.push(walletPaymentOption)
  }
  return paymentOptions
}
